

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
    Autocomplete
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewCustomerModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        allergy_condition: '',
        dietary_preference: '',
        address: '',
        passport_number: '',
        passport_issue_date: '',
        passport_expiry_date: '',
        passport_issuing_country: '',
        has_active_order: false,
        country: '',
        referral_code_id: null,
        travel_persona: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [referralCodes, setReferralCodes] = useState([]);
    const [loadingReferralCodes, setLoadingReferralCodes] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchReferralCodes();
        }
    }, [isOpen]);

    const fetchReferralCodes = async () => {
        setLoadingReferralCodes(true);
        try {
            const response = await axiosInstance.get('/referral-codes/');
            setReferralCodes(response.data || []);
        } catch (error) {
            console.error('Error fetching referral codes:', error);
        } finally {
            setLoadingReferralCodes(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                referral_code_id: formData.referral_code_id || null,
                passport_issue_date: formData.passport_issue_date || null,
                passport_expiry_date: formData.passport_expiry_date || null
            };
            await onSave(payload);
            setFormData({
                name: '',
                phone: '',
                email: '',
                allergy_condition: '',
                dietary_preference: '',
                address: '',
                passport_number: '',
                passport_issue_date: '',
                passport_expiry_date: '',
                passport_issuing_country: '',
                has_active_order: false,
                country: '',
                referral_code_id: null,
                travel_persona: ''
            });
            onClose();
        } catch (error) {
            console.error('Error saving customer:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving customer',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '80%', md: '800px' },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <MDBox className="pearl-popup-header"
                        sx={{
                            backgroundColor: colors.popupHeader.background,
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8
                        }}
                    >
                        <MDTypography variant="h6" sx={{ color: '#FFFFFF' }}>
                            New Customer
                        </MDTypography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon sx={{ color: '#FFFFFF' }} />
                        </IconButton>
                    </MDBox>
                    <MDBox p={3}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Country of Residence"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Address"
                                        name="address"
                                        multiline
                                        rows={2}
                                        value={formData.address}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Allergies"
                                        name="allergy_condition"
                                        multiline
                                        rows={2}
                                        value={formData.allergy_condition}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Dietary Preferences"
                                        name="dietary_preference"
                                        multiline
                                        rows={2}
                                        value={formData.dietary_preference}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Passport Number"
                                        name="passport_number"
                                        value={formData.passport_number}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Passport Issuing Country"
                                        name="passport_issuing_country"
                                        value={formData.passport_issuing_country}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Passport Issue Date"
                                        name="passport_issue_date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.passport_issue_date}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Passport Expiry Date"
                                        name="passport_expiry_date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.passport_expiry_date}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={referralCodes}
                                        getOptionLabel={(option) => option?.code || ''}
                                        loading={loadingReferralCodes}
                                        value={referralCodes.find(r => r.id === formData.referral_code_id) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, referral_code_id: newValue?.id || null }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Referral Code"
                                                variant="outlined"
                                                margin="normal"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingReferralCodes ? <CircularProgress size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Travel Persona"
                                        name="travel_persona"
                                        value={formData.travel_persona}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                            </Grid>
                            <MDBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
                                <MDButton variant="outlined" color="secondary" onClick={onClose}>
                                    Cancel
                                </MDButton>
                                <MDButton variant="gradient" color="info" type="submit" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={24} /> : 'Save'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </MDBox>
                </Box>
            </Modal>
            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity}>{toast.message}</Alert>
            </Snackbar>
        </>
    );
};

export default NewCustomerModal;

